import {
	Dialog,
	DialogTitle,
	DialogContent,
	IconButton,
	Typography,
	Stack,
	CircularProgress,
	Table,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
	TableFooter,
	TextField,
	InputAdornment,
	IconButton as MIconButton,
	Box,
	Chip,
	Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import UndoIcon from "@mui/icons-material/Undo";
import { useEffect, useState } from "react";
import { ComissionServices } from "../../../../shared/services/api/comission/ComissionServices";
import { format } from "date-fns";
import { useDebounce, useToast } from "../../../../shared/hooks";
import { ToastContent } from "../../../../shared/components";
import { checkByProfileGuard } from "../../../../shared/guards/ProfileGuard";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../../shared/enums/profiles";
import { useProfile } from "../../../../context/ProfileContext";
import {
	globalFormatCurrencyBRL,
	globalMaskDocument,
	globalTranslateStatusComission,
	globalTranslateTypeComission,
	translateProposal,
} from "../../../../shared/utils";
import { globalcolorBackGroundComission, globalcolorTextComision } from "../../../../shared/utils/global-utils-comission";
import { ModalConfirmSelect } from "./ModalConfirmSelect";

// Desfazer só faz sentido pra quem ainda não foi enviado a nenhum canal de pagamento — 'not_eligible'
// nunca chegou perto disso, 'open' só se ainda não estiver dentro de uma fatura já enviada à Livve
// (ver seção 5.1 do documento de especificação).
const canUndoCloseCycle = (item: Pick<Item, "status" | "invoiceGateway">) =>
	item.status === "not_eligible" || (item.status === "open" && !item.invoiceGateway);

interface Props {
	open: boolean;
	onClose: () => void;
	comissionId: string;
	dataIni: string;
	dataFim: string;
}

interface Item {
	id: string;
	clientId: string;
	clientName: string;
	clientDocument: string;
	proposalCertificateNumber: string | null;
	proposalName: string | null;
	type: string;
	installmentValue: string;
	description: string;
	value: string;
	dueDate: string;
	installmentNumber?: number;
	proposalRentMonthsCount?: number;
	proposalDocument?: string;
	status: string;
	settledCycle?: string;
	invoiceReference?: string;
	invoiceGateway?: string;
}

export const ModalDetailComission = ({ open, onClose, comissionId, dataIni, dataFim }: Props) => {
	const [items, setItems] = useState<Item[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [searchText, setSearchText] = useState("");
	const [debouncedSearchText, setDebouncedSearchText] = useState("");
	const [undoTarget, setUndoTarget] = useState<Item | null>(null);
	const [isUndoing, setIsUndoing] = useState(false);
	const { debounce } = useDebounce();
	const toastHandler = useToast();
	const { isSuperAdmin } = useProfile();
	// Ação granular Livve — pedido formalizado pro backend, ver GAPS_LIVVE_V2_PARA_BACKEND.md.
	const canUndo = isSuperAdmin || checkByProfileGuard(PermissionRolesEnum.FinancialOptions, PermissionActionsEnum.LivveUndo);

	const fetchData = async () => {
		setIsLoading(true);
		try {
			const result = await ComissionServices.getComissionValuesDetail(comissionId, dataIni, dataFim);
			setItems(Array.isArray(result.results) ? result.results : []);
		} catch (error) {
			console.error("Erro ao buscar detalhes:", error);
			setItems([]);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (open && comissionId) fetchData();
	}, [open, comissionId]);

	const handleConfirmUndo = async () => {
		if (!undoTarget) return;
		setIsUndoing(true);
		try {
			const result = await ComissionServices.undoCloseCycle(undoTarget.id);
			if (result instanceof Error) {
				toastHandler.present({
					type: "error",
					position: "top-right",
					messageOrContent: <ToastContent title="Erro" content={result.message} />,
				});
				return;
			}
			toastHandler.present({
				type: "success",
				position: "top-right",
				messageOrContent: <ToastContent title="Sucesso" content="Fechamento desfeito — parcela voltou pra Previsto." />,
			});
			setUndoTarget(null);
			await fetchData();
		} finally {
			setIsUndoing(false);
		}
	};

	useEffect(() => {
		debounce(() => setDebouncedSearchText(searchText));
	}, [searchText]);

	const normalize = (text: string) => text.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();

	const filteredItems = items.filter((item) => normalize(`${item.proposalName ?? ""} ${item.proposalDocument}`).includes(normalize(debouncedSearchText)));

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
			<DialogTitle
				sx={{
					color: "primary.main",
					fontWeight: 400,
					display: "flex",
					justifyContent: "space-between",
				}}
			>
				<Typography variant="h5">{`Detalhes do Faturamento - ${items[0]?.clientName?.trim() || ""}`}</Typography>
				<IconButton onClick={onClose}>
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<DialogContent dividers>
				{isLoading ? (
					<Stack alignItems="center" py={4}>
						<CircularProgress />
						<Typography variant="body2" mt={2}>
							Carregando...
						</Typography>
					</Stack>
				) : (
					<>
						<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
							<TextField
								placeholder="Pesquisar por nome ou documento"
								variant="outlined"
								size="small"
								fullWidth
								value={searchText}
								onChange={(e) => setSearchText(e.target.value)}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<SearchIcon />
										</InputAdornment>
									),
									endAdornment: searchText && (
										<InputAdornment position="end">
											<MIconButton size="small" onClick={() => setSearchText("")}>
												<ClearIcon fontSize="small" />
											</MIconButton>
										</InputAdornment>
									),
								}}
								sx={{ maxWidth: 400 }}
							/>

							<Typography variant="body2" sx={{ ml: 2 }}>
								{filteredItems.length} registro(s) encontrado(s)
							</Typography>
						</Stack>
						<Box sx={{ maxHeight: 420, overflowY: "auto" }}>
							<Table size="small">
								<TableHead>
									<TableRow sx={{ backgroundColor: "grey.100" }}>
										{["CERTIFICADO", "NOME", "TIPO", "VALOR", "VENCIMENTO", "OBSERVAÇÃO", "STATUS"].map((col, i) => (
											<TableCell key={i}>
												<Typography variant="button">{col}</Typography>
											</TableCell>
										))}
									</TableRow>
								</TableHead>

								<TableBody>
									{filteredItems.map((item) => (
										<TableRow key={item.id}>
											<TableCell>{item.proposalCertificateNumber ?? "-"}</TableCell>
											<TableCell>
												<Typography fontSize="0.875rem">{item.proposalName!!}</Typography>
												<Typography fontSize="0.875rem" fontWeight="bold">
													{globalMaskDocument(item.proposalDocument??'')}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography fontSize="0.875rem">{globalTranslateTypeComission(item.type)}</Typography>
												{item.type === "prize" && item.installmentNumber && item.proposalRentMonthsCount && (
													<Typography variant="caption" color="textSecondary">
														{item.installmentNumber}/{item.proposalRentMonthsCount}
													</Typography>
												)}
											</TableCell>
											<TableCell>
												<Typography fontWeight="bold">{globalFormatCurrencyBRL(item.value)}</Typography>
											</TableCell>
											<TableCell>{format(new Date(item.dueDate), "dd/MM/yyyy")}</TableCell>
											<TableCell>{item.description}</TableCell>

											<TableCell>
												<Stack direction="row" alignItems="center" spacing={0.5}>
													<Chip
														label={translateProposal(globalTranslateStatusComission(item.status))}
														size="small"
														variant="filled"
														sx={{
															backgroundColor: globalcolorBackGroundComission(item.status),
															color: globalcolorTextComision(item.status),
															fontWeight: "normal",
														}}
													/>
													{canUndo && canUndoCloseCycle(item) && (
														<Tooltip title="Desfazer fechamento — volta pra Previsto">
															<MIconButton size="small" onClick={() => setUndoTarget(item)}>
																<UndoIcon fontSize="small" />
															</MIconButton>
														</Tooltip>
													)}
												</Stack>
												{item.invoiceReference && (
													<Typography variant="caption" display="block" color="textSecondary" sx={{ mt: 0.5 }}>
														Fatura {item.invoiceReference}
														{item.invoiceGateway ? ` · ${item.invoiceGateway}` : ""}
													</Typography>
												)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>

								<TableFooter>
									<TableRow>
										<TableCell colSpan={7}>
											<Stack spacing={1} alignItems="flex-end" py={3}>
												<Typography variant="subtitle2" fontWeight={500} sx={{ mb: 1 }}>
													Resumo por Tipo de Comissão
												</Typography>

												<Table size="small" sx={{ width: 400, border: "1px solid #e0e0e0" }}>
													<TableHead>
														<TableRow sx={{ backgroundColor: "grey.100" }}>
															<TableCell>
																<Typography variant="caption" fontWeight={600}>
																	Tipo
																</Typography>
															</TableCell>
															<TableCell align="center">
																<Typography variant="caption" fontWeight={600}>
																	Quantidade
																</Typography>
															</TableCell>
															<TableCell align="right">
																<Typography variant="caption" fontWeight={600}>
																	Valor Total
																</Typography>
															</TableCell>
														</TableRow>
													</TableHead>
													<TableBody>
														{["prize", "bonus", "discount"].map((type) => {
															const label = type === "prize" ? "Premiação" : type === "bonus" ? "Bonificação" : "Estorno";

															// ✅ Somar apenas status === "preview"
															const entries = filteredItems.filter((i) => i.type === type && i.status === "preview");

															const count = entries.length;
															const total = entries.reduce((acc, i) => acc + Number(i.value), 0);

															return (
																<TableRow key={type}>
																	<TableCell>
																		<Typography variant="body2">{label}</Typography>
																	</TableCell>
																	<TableCell align="center">
																		<Typography variant="body2">{count}</Typography>
																	</TableCell>
																	<TableCell align="right">
																		<Typography variant="body2">{globalFormatCurrencyBRL(total.toString())}</Typography>
																	</TableCell>
																</TableRow>
															);
														})}
													</TableBody>
													<TableFooter>
														<TableRow sx={{ backgroundColor: "#f4fff4" }}>
															<TableCell>
																<Typography fontWeight={600}>Total</Typography>
															</TableCell>
															<TableCell />
															<TableCell align="right">
																<Typography fontWeight={700} color="primary">
																	{globalFormatCurrencyBRL(
																		filteredItems
																			.filter((i) => i.status === "preview")
																			.reduce((acc, i) => acc + Number(i.value), 0)
																			.toString()
																	)}
																</Typography>
															</TableCell>
														</TableRow>
													</TableFooter>
												</Table>
											</Stack>
										</TableCell>
									</TableRow>
								</TableFooter>
							</Table>
						</Box>
					</>
				)}
			</DialogContent>

			<ModalConfirmSelect
				open={Boolean(undoTarget)}
				onClose={() => setUndoTarget(null)}
				onConfirm={handleConfirmUndo}
				title="Desfazer fechamento"
				description={`A parcela volta pro status Previsto e entra na próxima apuração como se nunca tivesse sido decidida${
					undoTarget?.proposalName ? ` — ${undoTarget.proposalName}` : ""
				}.`}
				confirmLabel="Sim, desfazer"
				cancelLabel="Cancelar"
				isLoading={isUndoing}
			/>
		</Dialog>
	);
};
